// SVG path generators for the shape picker. Every generator is authored in
// a consistent 0 0 100 100 coordinate box centered on (50,50), so all
// resulting fabric.Path objects come out at a comparable natural size.

const toRad = (deg: number) => (deg * Math.PI) / 180;
const fx = (n: number) => n.toFixed(2);

type Point = [number, number];

const pointsToPath = (pts: Point[]) =>
  pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${fx(p[0])} ${fx(p[1])}`)
    .join(' ') + ' Z';

const rotatePoint = (
  [x, y]: Point,
  angleDeg: number,
  [cx, cy]: Point = [50, 50],
): Point => {
  const a = toRad(angleDeg);
  const dx = x - cx;
  const dy = y - cy;
  return [
    cx + dx * Math.cos(a) - dy * Math.sin(a),
    cy + dx * Math.sin(a) + dy * Math.cos(a),
  ];
};

// A circle drawn as an SVG subpath (4 cubic beziers). Useful as a standalone
// shape or combined with other subpaths (e.g. a ring, or a gear's hole).
const K = 0.5523;
export const circleSubpath = (cx: number, cy: number, r: number) =>
  `M ${fx(cx - r)} ${fx(cy)} ` +
  `C ${fx(cx - r)} ${fx(cy - r * K)} ${fx(cx - r * K)} ${fx(cy - r)} ${fx(
    cx,
  )} ${fx(cy - r)} ` +
  `C ${fx(cx + r * K)} ${fx(cy - r)} ${fx(cx + r)} ${fx(cy - r * K)} ${fx(
    cx + r,
  )} ${fx(cy)} ` +
  `C ${fx(cx + r)} ${fx(cy + r * K)} ${fx(cx + r * K)} ${fx(cy + r)} ${fx(
    cx,
  )} ${fx(cy + r)} ` +
  `C ${fx(cx - r * K)} ${fx(cy + r)} ${fx(cx - r)} ${fx(cy + r * K)} ${fx(
    cx - r,
  )} ${fx(cy)} Z`;

export const regularPolygonPath = (
  sides: number,
  r = 47,
  rotationDeg = -90,
) => {
  const pts: Point[] = [];
  for (let i = 0; i < sides; i++) {
    const angle = toRad(rotationDeg + (360 / sides) * i);
    pts.push([50 + r * Math.cos(angle), 50 + r * Math.sin(angle)]);
  }
  return pointsToPath(pts);
};

export const starPath = (
  points: number,
  outerR = 47,
  innerRatio = 0.5,
  rotationDeg = -90,
) => {
  const pts: Point[] = [];
  const innerR = outerR * innerRatio;
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outerR : innerR;
    const angle = toRad(rotationDeg + (180 / points) * i);
    pts.push([50 + r * Math.cos(angle), 50 + r * Math.sin(angle)]);
  }
  return pointsToPath(pts);
};

export const plusPath = (armWidthRatio = 0.32, reach = 47) => {
  const w = reach * armWidthRatio;
  const s = reach;
  const pts: Point[] = [
    [50 - w, 50 - s],
    [50 + w, 50 - s],
    [50 + w, 50 - w],
    [50 + s, 50 - w],
    [50 + s, 50 + w],
    [50 + w, 50 + w],
    [50 + w, 50 + s],
    [50 - w, 50 + s],
    [50 - w, 50 + w],
    [50 - s, 50 + w],
    [50 - s, 50 - w],
    [50 - w, 50 - w],
  ];
  return pointsToPath(pts);
};

export const xMarkPath = (armWidthRatio = 0.24, reach = 47) => {
  const w = reach * armWidthRatio;
  const s = reach;
  const raw: Point[] = [
    [50 - w, 50 - s],
    [50 + w, 50 - s],
    [50 + w, 50 - w],
    [50 + s, 50 - w],
    [50 + s, 50 + w],
    [50 + w, 50 + w],
    [50 + w, 50 + s],
    [50 - w, 50 + s],
    [50 - w, 50 + w],
    [50 - s, 50 + w],
    [50 - s, 50 - w],
    [50 - w, 50 - w],
  ];
  const rotated = raw.map((p) => rotatePoint(p, 45));
  return pointsToPath(rotated);
};

// Arrow pointing up by default; rotate 90/180/270 for right/down/left.
export const arrowPath = (
  direction: 'up' | 'down' | 'left' | 'right' = 'up',
  headWidthRatio = 0.9,
  shaftWidthRatio = 0.32,
) => {
  const hw = 47 * headWidthRatio;
  const sw = 47 * shaftWidthRatio;
  const pts: Point[] = [
    [50, 3],
    [50 + hw, 45],
    [50 + sw, 45],
    [50 + sw, 97],
    [50 - sw, 97],
    [50 - sw, 45],
    [50 - hw, 45],
  ];
  const rotationByDirection = { up: 0, right: 90, down: 180, left: 270 };
  const rotated = pts.map((p) =>
    rotatePoint(p, rotationByDirection[direction]),
  );
  return pointsToPath(rotated);
};

export const trapezoidPath = (topWidthRatio = 0.5) => {
  const topHalf = 47 * topWidthRatio;
  return pointsToPath([
    [50 - topHalf, 15],
    [50 + topHalf, 15],
    [95, 85],
    [5, 85],
  ]);
};

export const parallelogramPath = (skewRatio = 0.3) => {
  const skew = 90 * skewRatio;
  return pointsToPath([
    [5 + skew, 15],
    [95, 15],
    [95 - skew, 85],
    [5, 85],
  ]);
};

export const kitePath = (widthRatio = 0.6, waistRatio = 0.42) => {
  const halfW = 47 * widthRatio;
  const waistY = 100 * waistRatio;
  return pointsToPath([
    [50, 2],
    [50 + halfW, waistY],
    [50, 98],
    [50 - halfW, waistY],
  ]);
};

export const halfCirclePath = (
  flip: 'top' | 'bottom' | 'left' | 'right' = 'top',
) => {
  const base = `M 5 50 A 45 45 0 0 1 95 50 Z`;
  const rotationByFlip = { top: 0, right: 90, bottom: 180, left: 270 };
  if (rotationByFlip[flip] === 0) return base;
  // Rebuild via rotated endpoints for non-default orientations.
  const p1 = rotatePoint([5, 50], rotationByFlip[flip]);
  const p2 = rotatePoint([95, 50], rotationByFlip[flip]);
  return `M ${fx(p1[0])} ${fx(p1[1])} A 45 45 0 0 1 ${fx(p2[0])} ${fx(
    p2[1],
  )} Z`;
};

export const quarterCirclePath = () => `M 5 95 L 5 5 A 90 90 0 0 1 95 95 Z`;

export const archPath = (legHeightRatio = 0.45) => {
  const legTopY = 100 * (1 - legHeightRatio) - 5;
  return `M 10 95 L 10 ${fx(legTopY)} A 40 40 0 0 1 90 ${fx(
    legTopY,
  )} L 90 95 Z`;
};

// Smooth n-lobe scalloped outline - used for flowers, seals/bursts, clovers.
export const scallopPath = (
  lobes = 8,
  baseR = 44,
  amplitude = 8,
  steps = 240,
) => {
  const pts: Point[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * 360;
    const r = baseR + amplitude * Math.cos(toRad(lobes * t));
    const angle = toRad(t - 90);
    pts.push([50 + r * Math.cos(angle), 50 + r * Math.sin(angle)]);
  }
  return pointsToPath(pts);
};

export const gearPath = (teeth = 8, outerR = 46, innerR = 36, holeR = 14) => {
  const pts: Point[] = [];
  const toothAngle = 360 / teeth;
  for (let i = 0; i < teeth; i++) {
    const base = i * toothAngle;
    const a0 = toRad(base - 90);
    const a1 = toRad(base + toothAngle * 0.28 - 90);
    const a2 = toRad(base + toothAngle * 0.55 - 90);
    const a3 = toRad(base + toothAngle * 0.83 - 90);
    pts.push([50 + innerR * Math.cos(a0), 50 + innerR * Math.sin(a0)]);
    pts.push([50 + outerR * Math.cos(a1), 50 + outerR * Math.sin(a1)]);
    pts.push([50 + outerR * Math.cos(a2), 50 + outerR * Math.sin(a2)]);
    pts.push([50 + innerR * Math.cos(a3), 50 + innerR * Math.sin(a3)]);
  }
  return `${pointsToPath(pts)} ${circleSubpath(50, 50, holeR)}`;
};

export const pacmanPath = (mouthDeg = 60, r = 46) => {
  const startAngle = mouthDeg / 2;
  const endAngle = 360 - mouthDeg / 2;
  const p1: Point = [
    50 + r * Math.cos(toRad(startAngle - 90)),
    50 + r * Math.sin(toRad(startAngle - 90)),
  ];
  const p2: Point = [
    50 + r * Math.cos(toRad(endAngle - 90)),
    50 + r * Math.sin(toRad(endAngle - 90)),
  ];
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M 50 50 L ${fx(p1[0])} ${fx(p1[1])} A ${r} ${r} 0 ${largeArc} 1 ${fx(
    p2[0],
  )} ${fx(p2[1])} Z`;
};

export const ringPath = (outerR = 46, innerR = 30) =>
  `${circleSubpath(50, 50, outerR)} ${circleSubpath(50, 50, innerR)}`;

export const bookmarkPath = () =>
  pointsToPath([
    [12, 3],
    [88, 3],
    [88, 97],
    [50, 70],
    [12, 97],
  ]);

export const chevronArrowPath = () =>
  pointsToPath([
    [8, 15],
    [58, 15],
    [92, 50],
    [58, 85],
    [8, 85],
    [38, 50],
  ]);
